import {
  ArrowUp,
  ArrowDown,
  IndianRupee,
  BadgeIndianRupee,
  HandCoins,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import RevenueMonitoring from "../../components/AdminPortal/Finance/RevenueMonitoring";
import ExpenseTracking from "../../components/AdminPortal/Finance/ExpenseTracking";
import MarketingCosts from "../../components/AdminPortal/Finance/MarketingCosts";
import FixedCosts from "../../components/AdminPortal/Finance/FixedCosts";
import InvoiceManagement from "../../components/AdminPortal/Finance/InvoiceManagement";
import { getFinancialOverview } from "../../services/AdminPortal/financeAPI";

const FinancialTracking = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const [financeData, setFinanceData] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    totalProfit: 0,
    pendingLoanRepayment: 0,
    trends: {
      revenue: 0,
      expenses: 0,
      profit: 0,
      repayment: 0,
    }
  });
  const [loading, setLoading] = useState(true);
  const [timing, setTiming] = useState('all');

  useEffect(() => {
    fetchFinanceData();
  }, []);

  useEffect(() => {
    fetchFinanceData();
  }, [timing]);

  const fetchFinanceData = async () => {
    try {
      console.log('Fetching finance data with timing:', timing);
      const data = await getFinancialOverview({ 
        repaymentTiming: timing,
        revenueTiming: timing, // Filter by invoice/earned date
        expenseTiming: timing, // Filter by bill/expense incurred date
        profitTiming: timing   // Calculated as Revenue - Expenses
      });
      console.log('Received finance data:', data);
      setFinanceData(data);
    } catch (error) {
      console.error("Error fetching finance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `₹${value?.toLocaleString("en-IN") || "0"}`;

  const stats = [
    {
      label: "TOTAL REVENUE",
      value: formatCurrency(financeData.totalRevenue),
      trend: financeData.trends?.revenue || 0,
      icon: <IndianRupee className="text-black" />,
    },
    {
      label: "TOTAL EXPENSES",
      value: formatCurrency(financeData.totalExpenses),
      trend: financeData.trends?.expenses || 0,
      icon: <ArrowDown className="text-black" />,
    },
    {
      label: "TOTAL PROFIT",
      value: formatCurrency(financeData.totalProfit),
      trend: financeData.trends?.profit || 0,
      icon: <BadgeIndianRupee className="text-black" />,
    },
    {
      label: "PENDING LOAN REPAYMENT",
      value: formatCurrency(financeData.pendingLoanRepayment),
      trend: financeData.trends?.repayment || 0,
      icon: <HandCoins className="text-black" />,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-[300px] bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-white mt-12 p-4 md:p-6"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 w-full">
        <h1 className="text-xl font-medium mb-3 sm:mb-0">Finance Tracking</h1>
        <select
          value={timing}
          onChange={(e) => {
            console.log('Timing changed to:', e.target.value);
            setTiming(e.target.value);
          }}
          className="bg-[#1a1a1a] text-white border border-[#444] rounded px-3 py-2 focus:outline-none"
        >
          <option value="all">All time</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
        {stats.map((item, idx) => (
          <div
            key={idx}
            className="border border-[#333333] bg-[#131313] p-4 rounded-lg shadow"
          >
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0 pr-2">
                <div className="text-xs text-gray-400 truncate">{item.label}</div>
                <div className="text-base lg:text-2xl font-bold mt-2">{item.value}</div>
                <div className="flex items-center mt-1 text-xs sm:text-sm text-gray-400">
                  <span className="truncate"> {timing === 'weekly' ? 'last week' : timing === 'quarterly' ? 'last quarter' : timing === 'all' ? 'all time' : 'last month'}</span>
                </div>
              </div>
              <div className="bg-yellow-400 rounded-full p-3 lg:p-3 flex-shrink-0">
                {React.cloneElement(item.icon, { size: 18, className: "text-black lg:w-6 lg:h-6" })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Business Cards */}
      <div className="mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div className="col-span-1 lg:col-span-3 h-full overflow-hidden">
            <RevenueMonitoring onDataChange={fetchFinanceData} />
          </div>

          <div className="col-span-1 lg:col-span-3 h-full overflow-hidden">
            <ExpenseTracking onDataChange={fetchFinanceData} />
          </div>

          <div className="col-span-1 lg:col-span-2 h-full overflow-hidden">
            <MarketingCosts onDataChange={fetchFinanceData} />
          </div>
        </div>
      </div>

      <FixedCosts onDataChange={fetchFinanceData} />

      <InvoiceManagement />
    </div>
  );
};

export default FinancialTracking;